import {
  PrimaryButton,
  SecondaryButton,
  Banner,
  PrimaryButtonProps,
  SecondaryButtonProps,
} from "@brexhq/component-library";
import styled from "@emotion/styled";
import { m } from "framer-motion";
import React, { useMemo } from "react";

import {
  ErrorMessage,
  StepTransitionDirection,
  useFormSubmissionContext,
} from "@brexhq/next/src/components/modules/LeadForm/FormSubmissionProvider";
import { Form } from "@brexhq/next/src/components/modules/LeadForm/Steps/elements";
import { LeadFormSteps } from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import { BlockRows } from "@brexhq/next/src/components/shared/BlockRows/BlockRows";
import { ErrorBoundary } from "@brexhq/next/src/components/shared/ErrorBoundary";
import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import {
  FormStepViewedEvent,
  LeadFormSubmittedEvent,
} from "@brexhq/next/src/telemetry/analyticsEvents";
import { TitleXS } from "@brexhq/shared/components/Typography/Components";
import { Text } from "@brexhq/shared/components/Typography/Text";

import { useLeadFormModuleContext } from "../../LeadFormModuleProvider";
import { SignInNavCTA } from "../../LeadFormNav";
import { getSequence } from "../../machines/helpers";

import {
  TitleContainer,
  SubTitle,
  ErrorBannerWrapper,
  TakeoverBottomNavWrapper,
  PageTemplateWrapper,
  TakeoverBottomNavContent,
  TakeoverContent,
} from "./PageTemplate.elements";

export type ActionMeta = {
  label?: string;
  disabled: boolean;
  loading: boolean;
  onClick: (event?: React.MouseEvent<HTMLElement>) => void;
};
export interface SalesPageTemplateProps {
  title: string;
  step: LeadFormSteps;
  subtitle?: string | React.ReactNode;
  showSignIn?: boolean;
  programName: string;
  nextAction: ActionMeta;
  prevAction?: Pick<ActionMeta, "label" | "onClick">;
  error?: ErrorMessage | null;
  stepTransitionDirection: StepTransitionDirection;
}

const FullWidthPrimary = styled(PrimaryButton)<PrimaryButtonProps>(
  ({ theme }) => ({
    display: "none",
    [theme.breakpoints.phone]: {
      display: "inline-block",
    },
  }),
);

const FullWidthSecondary = styled(SecondaryButton)<SecondaryButtonProps>(
  ({ theme }) => ({
    display: "none",
    [theme.breakpoints.phone]: {
      display: "inline-block",
    },
  }),
);

const PrimaryBtn = styled(PrimaryButton)<PrimaryButtonProps>(({ theme }) => ({
  [theme.breakpoints.phone]: {
    display: "none",
  },
}));

const SecondaryBtn = styled(SecondaryButton)<SecondaryButtonProps>(
  ({ theme }) => ({
    [theme.breakpoints.phone]: {
      display: "none",
    },
  }),
);

const FullWidth = styled.div(({ theme }) => ({
  [theme.breakpoints.phone]: {
    width: "100%",
  },
}));

const ButtonExtraContentWrapper = styled.div<{ isLeft?: boolean }>(
  ({ theme, isLeft }) => ({
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: isLeft ? "flex-start" : "flex-end",
    gap: theme.spacing[16],
    [theme.breakpoints.phone]: {
      gap: theme.spacing[4],
      flexDirection: isLeft ? "column-reverse" : "column",
      justifyContent: "center",
      ...(isLeft && {
        alignItems: "flex-start",
      }),
    },
  }),
);

const StepIndicatorWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing[4],
  [theme.breakpoints.phone]: {
    display: "none",
  },
}));

const TakeoverButtonsWrapper = styled.div({
  display: "flex",
  flexDirection: "row",
  width: "100%",
});

const TakeoverContentWrapper = styled.div(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.phone]: {
    gap: theme.spacing[8],
  },
}));

const StepIndicatorText = styled(Text)(({ theme }) => ({
  ...theme.typography.BodySmallM,
}));

const StepIndicatorSecondaryText = styled(Text)(({ theme }) => ({
  ...theme.typography.BodySmallR,
  color: theme.colors.content.disabled,
}));

const StepIndicator = () => {
  const { form, stateMachineCtx } = useFormSubmissionContext();

  return (
    <StepIndicatorWrapper>
      <StepIndicatorText as="span">
        Step {getSequence(stateMachineCtx).indexOf(form.currentState) + 1}
      </StepIndicatorText>
      <StepIndicatorSecondaryText as="span">
        of {getSequence(stateMachineCtx).length}
      </StepIndicatorSecondaryText>
    </StepIndicatorWrapper>
  );
};

const SignInCTAMobile = styled.div(({ theme }) => ({
  display: "none",
  [theme.breakpoints.phone]: {
    display: "block",
    "& > div": {
      justifySelf: "flex-start",
    },
  },
}));

const SignInCTADesktop = styled.div(({ theme }) => ({
  display: "block",
  [theme.breakpoints.phone]: {
    display: "none",
  },
}));

const FORM_ANIMATION_VARIANTS = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0,
    };
  },
};

export const SalesPageTemplate: React.FC<
  React.PropsWithChildren<SalesPageTemplateProps>
> = ({
  title,
  subtitle,
  children,
  step,
  programName,
  nextAction,
  prevAction,
  error,
  stepTransitionDirection,
}) => {
  /**
   * TODO: Add progress bar props
   */
  const { form } = useFormSubmissionContext();
  const { navigationConfig, formSettings, theme } = useLeadFormModuleContext();
  const { trackEvent } = useAnalytics();
  React.useEffect(() => {
    const formValues = form?.getValues() || {};
    window.scrollTo(0, 0);
    trackEvent(new FormStepViewedEvent(step, programName, formValues));

    // Disabling below because we want this to run once and only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const next = React.useCallback(
    (event) => {
      const formValues = form?.getValues() || {};
      trackEvent(new LeadFormSubmittedEvent(step, programName, formValues));
      nextAction.onClick(event);
    },
    [nextAction, programName, step, trackEvent, form],
  );

  const prev = React.useCallback(() => {
    prevAction?.onClick();
  }, [prevAction]);

  const signInNavCTA = useMemo(() => {
    if (navigationConfig == null) return null;
    if (navigationConfig?.disableSignIn === true) return null;
    if (navigationConfig?.signInButtonLocation === "top") return null;
    return (
      <SignInNavCTA
        signInText={navigationConfig?.signInText ?? "Have an account?"}
        signInButtonText={navigationConfig?.signInButtonText ?? "Sign in"}
        signInUrl={
          navigationConfig?.signInUrl ??
          process.env.DASHBOARD_ORIGIN_URL ??
          "https://dashboard.brex.com/" // Fallback incase undefined
        }
        ctaType="link"
      />
    );
  }, [navigationConfig]);

  return (
    <PageTemplateWrapper
      id="signup-form-content-container"
      backgroundColor={formSettings?.backgroundColor}
    >
      <TakeoverContent reduceSpacing={navigationConfig != null}>
        <Form
          backgroundColor={formSettings?.backgroundColor}
          hasBorder={formSettings?.hasBorder}
          hasShadow={formSettings?.hasShadow}
          padding={formSettings?.padding}
          onSubmit={(e) => {
            e.preventDefault();
            next(e);
          }}
        >
          <ErrorBoundary>
            <m.div
              {...(formSettings?.disableAnimation === true
                ? {}
                : {
                    variants: FORM_ANIMATION_VARIANTS,
                    custom: stepTransitionDirection,
                    initial: "enter",
                    animate: "center",
                    transition: {
                      x: { type: "spring", stiffness: 300, damping: 30 },
                      opacity: { duration: 0.5 },
                    },
                  })}
            >
              <TitleContainer>
                <TitleXS className="_lr-hide dd-privacy-mask">{title}</TitleXS>
                {subtitle && <SubTitle>{subtitle}</SubTitle>}
              </TitleContainer>
              {children}
            </m.div>
            {error && (
              <ErrorBannerWrapper>
                <Banner variant="error" title={error.title}>
                  {error.message}
                </Banner>
              </ErrorBannerWrapper>
            )}
          </ErrorBoundary>
          <TakeoverBottomNavWrapper>
            <TakeoverBottomNavContent
              stepNavigationMaxWidth={navigationConfig?.stepNavigationMaxWidth}
            >
              <TakeoverContentWrapper id="takeover-content-wrapper">
                {prevAction != null && (
                  <SignInCTAMobile>{signInNavCTA}</SignInCTAMobile>
                )}
                <TakeoverButtonsWrapper id="takeover-buttons-wrapper">
                  <ButtonExtraContentWrapper isLeft={true}>
                    <FullWidth>
                      {prevAction != null ? (
                        <>
                          <SecondaryBtn onClick={prev}>
                            {prevAction.label}
                          </SecondaryBtn>
                          <FullWidthSecondary isFullWidth onClick={prev}>
                            {prevAction.label}
                          </FullWidthSecondary>
                        </>
                      ) : (
                        <SignInCTAMobile>{signInNavCTA}</SignInCTAMobile>
                      )}
                    </FullWidth>
                    {navigationConfig?.showStepProgress && <StepIndicator />}
                  </ButtonExtraContentWrapper>
                  <ButtonExtraContentWrapper isLeft={false}>
                    {navigationConfig?.signInButtonLocation === "bottom" && (
                      <SignInCTADesktop>{signInNavCTA}</SignInCTADesktop>
                    )}
                    <FullWidth>
                      <PrimaryBtn
                        disabled={nextAction.disabled}
                        loading={nextAction.loading}
                        type="submit"
                      >
                        {nextAction.label}
                      </PrimaryBtn>
                      <FullWidthPrimary
                        isFullWidth
                        disabled={nextAction.disabled}
                        loading={nextAction.loading}
                        onClick={next}
                      >
                        {nextAction.label}
                      </FullWidthPrimary>
                    </FullWidth>
                  </ButtonExtraContentWrapper>
                </TakeoverButtonsWrapper>
              </TakeoverContentWrapper>
            </TakeoverBottomNavContent>
          </TakeoverBottomNavWrapper>
        </Form>
        {formSettings?.formExtra != null &&
          formSettings?.formExtra?.textRows.length > 0 && (
            <BlockRows
              rows={formSettings?.formExtra?.textRows}
              theme={theme}
              alignment="left"
            />
          )}
      </TakeoverContent>
    </PageTemplateWrapper>
  );
};
